import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

type SnowEffectProviderProps = {
  children: ReactNode;
  defaultEnabled?: boolean;
  storageKey?: string;
};

type SnowEffectProviderState = {
  enabled: boolean;
  setEnabled: (value: boolean) => void;
  toggle: () => void;
};

const SnowEffectContext = createContext<SnowEffectProviderState | undefined>(
  undefined,
);

const DEFAULT_STORAGE_KEY = 'snow-effect-enabled';

const readStoredValue = (storageKey: string, fallback: boolean) => {
  if (typeof window === 'undefined') {
    return fallback;
  }

  try {
    const storedValue = window.localStorage.getItem(storageKey);

    if (storedValue === null) {
      return fallback;
    }

    return storedValue === 'true';
  } catch {
    return fallback;
  }
};

const writeStoredValue = (storageKey: string, value: boolean) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(storageKey, String(value));
  } catch {
    // Swallow storage errors (e.g., private mode).
  }
};

export const SnowEffectProvider = ({
  children,
  defaultEnabled = true,
  storageKey = DEFAULT_STORAGE_KEY,
}: SnowEffectProviderProps) => {
  const [enabled, setEnabledState] = useState<boolean>(() =>
    readStoredValue(storageKey, defaultEnabled),
  );

  useEffect(() => {
    setEnabledState(readStoredValue(storageKey, defaultEnabled));
  }, [defaultEnabled, storageKey]);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== storageKey) {
        return;
      }

      if (event.newValue === null) {
        setEnabledState(defaultEnabled);
        return;
      }

      setEnabledState(event.newValue === 'true');
    };

    window.addEventListener('storage', handleStorage);

    return () => window.removeEventListener('storage', handleStorage);
  }, [defaultEnabled, storageKey]);

  const setEnabled = useCallback(
    (value: boolean) => {
      setEnabledState(value);
      writeStoredValue(storageKey, value);
    },
    [storageKey],
  );

  const toggle = useCallback(() => {
    setEnabledState((prev) => {
      const nextValue = !prev;
      writeStoredValue(storageKey, nextValue);
      return nextValue;
    });
  }, [storageKey]);

  const value = useMemo(
    () => ({
      enabled,
      setEnabled,
      toggle,
    }),
    [enabled, setEnabled, toggle],
  );

  return (
    <SnowEffectContext.Provider value={value}>
      {children}
    </SnowEffectContext.Provider>
  );
};

export const useSnowEffect = () => {
  const context = useContext(SnowEffectContext);

  if (context === undefined) {
    throw new Error('useSnowEffect must be used within a SnowEffectProvider');
  }

  return context;
};
